import { useDispatch, useSelector } from 'react-redux';
import {
  setListing,
  setSelected,
  setFilters,
  setSelectedFilters,
  setNextToken,
} from '../store/ducks/conversation';
import { HeadCell } from '../models/dataTable';
import useApp from './useApp';
import {
  CreateVariables,
  ConceptGetVariables,
  ConceptListingVariables,
  Option,
} from '../models/app';
import { getConversation, listConversations, conversationsByConceptId } from '../graphql/queries';
import { createConversation, updateConversation } from '../graphql/mutations';
import { generateClient } from '@aws-amplify/api';
import { toConversationDateRange } from '../helpers/utils';

const client = generateClient();

const LOG_PREFIX = '[Conversations]';

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  // const generalFilters = useSelector((state: any) => state.generalFilters);

  const listing = useSelector(
    (state: any) => state[`${listingName}`]?.['listing'] || [],
  );
  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]?.['nextToken'] || null,
  );

  /** Normalize to a concept ID string (handles both raw ID and object with .id from CustomSelect) */
  function toConceptId(value: string | { id?: string } | undefined | null): string | undefined {
    if (value == null || value === '') return undefined;
    return typeof value === 'string' ? value : (value?.id ?? undefined);
  }

  async function fetch(params: ConceptListingVariables, isAuth = true) {
    const { limit, searchText, fromDate, toDate, conceptID, conceptsSelectedFilters } = params;

    try {
      const filter: any = {
        deleted: { eq: '0' },
      };

      // Normalize concept selection: CustomSelect passes array of concept objects { id, name }; support IDs or objects
      const conceptIds: string[] = [];
      if (conceptsSelectedFilters && conceptsSelectedFilters.length > 0) {
        conceptsSelectedFilters.forEach((item: string | { id?: string }) => {
          const id = typeof item === 'string' ? item : item?.id;
          if (id) conceptIds.push(id);
        });
      } else {
        const singleId = toConceptId(conceptID as string | { id?: string } | undefined);
        if (singleId) conceptIds.push(singleId);
      }

      // Build concept filters from normalized IDs
      const conceptFilters: any[] = [];
      if (conceptIds.length > 0) {
        conceptFilters.push(...conceptIds.map((id) => ({ conceptId: { eq: id } })));
      }

      // Build search text filters
      const searchFilters: any[] = [];
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        searchFilters.push({ guestName: { contains: searchLower } });
        searchFilters.push({ guestPhoneNumber: { contains: searchLower } });
        searchFilters.push({ confirmationNumber: { contains: searchLower } });
        searchFilters.push({ guestEmail: { contains: searchLower } });
      }

      // Combine filters properly
      if (conceptFilters.length > 0 && searchFilters.length > 0) {
        // Both concept and search filters: use 'and' to combine them
        filter.and = [
          { or: conceptFilters },
          { or: searchFilters },
        ];
      } else if (conceptFilters.length > 0) {
        // Only concept filters
        if (conceptFilters.length === 1) {
          // Single concept, use direct filter
          filter.conceptId = { eq: conceptIds[0] };
        } else {
          // Multiple concepts, use 'or'
          filter.or = conceptFilters;
        }
      } else if (searchFilters.length > 0) {
        // Only search filters
        filter.or = searchFilters;
      }

      // Date Filter: include conversations where createdAt OR updatedAt is in range (so "created 16, updated 17" appears when selecting 17)
      const dateRangeNorm = toConversationDateRange(fromDate, toDate);
      if (dateRangeNorm) {
        const { apiFrom: apiFromDate, apiTo: apiToDate } = dateRangeNorm;
        console.log(`${LOG_PREFIX} date range selected:`, {
          fromDate,
          toDate,
          apiFromDate,
          apiToDate,
          conceptID: conceptID ?? null,
          conceptsSelectedFilters: conceptsSelectedFilters ?? null,
        });
        const dateFilter = {
          or: [
            { createdAt: { between: [apiFromDate, apiToDate] } },
            { updatedAt: { between: [apiFromDate, apiToDate] } },
          ],
        };

        // If 'and' filter already exists, add date filter to it
        if (filter.and) {
          filter.and.push(dateFilter);
        } else if (filter.or) {
          // If only 'or' exists, combine it with date filter using 'and'
          filter.and = [{ or: filter.or }, dateFilter];
          delete filter.or;
        } else {
          // No complex filters, add date filter directly
          filter.or = dateFilter.or;
        }
      }

      const conversationListing: any = await client.graphql({
        query: listConversations,
        variables: {
          filter,
          limit: 100000,
        },
        authMode: isAuth ? 'userPool' : 'iam',
      });

      const currentNextToken =
        conversationListing.data.listConversations.nextToken;
      const listing: any = conversationListing.data.listConversations.items;

      if (fromDate != null || toDate != null) {
        console.log(`${LOG_PREFIX} fetch result:`, {
          dateRange: dateRangeNorm ? { fromDate, toDate, apiFrom: dateRangeNorm.apiFrom, apiTo: dateRangeNorm.apiTo } : 'none',
          count: listing?.length ?? 0,
          hasNextToken: !!currentNextToken,
        });
      }

      dispatch(setNextToken(currentNextToken));
      dispatch(setListing(listing));
      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message || err);
      return [];
    }
  }

  async function get(params: ConceptGetVariables) {
    const { id, listing } = params;

    try {
      let single: any | undefined;
      if (listing.length > 0) {
        single = listing.find((resource: any) => resource.id === id);
      } else {
        const conversation: any = await client.graphql({
          query: getConversation,
          variables: { id },
          authMode: 'userPool',
        });
        single = conversation.data.getConversation;
      }

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(params: CreateVariables) {
    const { userID, userName, data } = params;
    // Handle both conceptID (from form) and conceptId (expected format)
    const conceptId = data.conceptId || data.conceptID;
    if (!conceptId) {
      const error = new Error(`Cannot create ${singleName} without conceptId`);
      return showError(error);
    }

    if (!data.guestPhoneNumber) {
      const error = new Error(
        `Cannot create ${singleName} without guestPhoneNumber`,
      );
      return showError(error);
    }

    try {
      const createInput: any = {
        conceptId: conceptId,
        guestPhoneNumber: data.guestPhoneNumber,
        whatsappId: data.whatsappId || null,
        guestName: data.guestName || null,
        reservationId: data.reservationId || null,
        confirmationNumber: data.confirmationNumber || null,
        status: data.status || 'active',
        assignedToId: data.assignedToId || null,
        assignedToName: data.assignedToName || null,
        source: data.source || '',
        language: data.language || null,
        notes: data.notes || null,
        tags: data.tags || [],
        deleted: '0',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      const result = await client.graphql({
        query: createConversation,
        variables: { input: createInput },
        authMode: 'userPool',
      });

      // Refresh the listing to include the new item
      await fetch(
        {
          accountID: '',
          conceptID: conceptId,
          searchText: '',
          startIndex: 0,
          limit: 1000,
          moveForward: true,
        },
        true,
      );

      showConfirm(`New ${singleName} has been created successfully`);
      return true;
    } catch (err) {
      showError(err);
      return false;
    }
  }

  async function update(params: any) {
    const { id, listing, data } = params;

    try {
      const original: any = await get({ id, listing });

      if (!original) {
        showError(`Invalid ${singleName} ID`);
        return;
      }

      const updateInput: any = {
        id: original.id,
        conceptId:
          data.conceptId !== undefined ? data.conceptId : original.conceptId,
        guestPhoneNumber:
          data.guestPhoneNumber !== undefined
            ? data.guestPhoneNumber
            : original.guestPhoneNumber,
        whatsappId:
          data.whatsappId !== undefined ? data.whatsappId : original.whatsappId,
        guestName:
          data.guestName !== undefined ? data.guestName : original.guestName,
        reservationId:
          data.reservationId !== undefined
            ? data.reservationId
            : original.reservationId,
        confirmationNumber:
          data.confirmationNumber !== undefined
            ? data.confirmationNumber
            : original.confirmationNumber,
        status: data.status !== undefined ? data.status : original.status,
        assignedToId:
          data.assignedToId !== undefined
            ? data.assignedToId
            : original.assignedToId,
        assignedToName:
          data.assignedToName !== undefined
            ? data.assignedToName
            : original.assignedToName,
        source: data.source !== undefined ? data.source : original.source,
        language:
          data.language !== undefined ? data.language : original.language,
        notes: data.notes !== undefined ? data.notes : original.notes,
        tags: data.tags !== undefined ? data.tags : original.tags,
        updatedAt: new Date().toISOString(),
      };

      await client.graphql({
        query: updateConversation,
        variables: { input: updateInput },
        authMode: 'userPool',
      });

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(params: ConceptGetVariables) {
    try {
      const { id, listing } = params;

      const original: any = await get(params);

      if (original) {
        const updateInput: any = {
          id: original.id,
          deleted: '1',
          updatedAt: new Date().toISOString(),
        };

        await client.graphql({
          query: updateConversation,
          variables: { input: updateInput },
          authMode: 'userPool',
        });

        dispatch(
          setListing(listing.filter((resource: any) => resource.id !== id)),
        );
      }

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function remove(params: ConceptGetVariables) {
    const { id, listing } = params;
    try {
      await client.graphql({
        query: `
          mutation DeleteConversation($input: DeleteConversationInput!) {
            deleteConversation(input: $input) {
              id
              __typename
            }
          }
        `,
        variables: { input: { id } },
        authMode: 'userPool',
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id)),
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      showError(err);
    }
  }

  async function exportAll(params: ConceptListingVariables) {
    const data = await fetch(params);
    return data;
  }

  const getName = (params: ConceptGetVariables) => {
    const { id, listing } = params;

    if (listing.length > 0) {
      const model = listing.find((model: any) => model.id === id);
      return model ? model.guestName || model.guestPhoneNumber : '';
    }

    return '';
  };

  function options(listing: any[]) {
    const options: Option[] = [];

    for (let option of listing) {
      options.push({
        label: `${option.guestName || option.guestPhoneNumber} - ${
          option.status || 'active'
        }`,
        value: option.id,
      });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'confirmationNumber',
      numeric: false,
      disablePadding: false,
      label: 'Confirmation #',
    },
    {
      id: 'guestName',
      numeric: false,
      disablePadding: false,
      label: 'Guest Name',
    },
    {
      id: 'guestEmail',
      numeric: false,
      disablePadding: false,
      label: 'Email',
    },
    {
      id: 'guestPhoneNumber',
      numeric: false,
      disablePadding: false,
      label: 'Phone Number',
    },
    {
      id: 'channel',
      numeric: false,
      disablePadding: false,
      label: 'Channel',
    },
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: 'Status',
    },
    {
      id: 'lastMessageAt',
      numeric: false,
      disablePadding: false,
      label: 'Last Message',
    },
    {
      id: 'createdBy',
      numeric: false,
      disablePadding: false,
      label: 'Created By',
    },
    {
      id: 'createdAt',
      numeric: false,
      disablePadding: false,
      label: 'Date',
    },
    {
      id: 'actions',
      numeric: true,
      disablePadding: false,
      label: '',
    },
  ];

  const dataCells: readonly string[] = [
    'confirmationNumber',
    'guestName',
    'guestEmail',
    'guestPhoneNumber',
    'channel',
    'status',
    'lastMessageAt',
  ];

  const api: any = {};

  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}Export`] = exportAll;
  api[`${listingName}GetName`] = getName;
  api[`${listingName}ChangeListing`] = (listing: any[]) => {
    dispatch(setListing(listing));
    dispatch(setFilters(listing.map((model: any) => model.status)));
  };

  api[`${listingName}ChangeSelected`] = (id: string) => {
    dispatch(setSelected(id));
  };
  api[`${listingName}ChangeFilters`] = (filters: string[]) =>
    dispatch(setFilters(filters));
  api[`${listingName}ChangeSelectedFilters`] = (filters: any) => {
    dispatch(setSelectedFilters(filters));
  };
  api[`${listingName}NextToken`] = nextToken;
  api[`${listingName}Listing`] = listing;
  api[`${listingName}ClearListing`] = () => dispatch(setListing([]));
  api[`${listingName}ClearNextToken`] = () => dispatch(setNextToken(null));
  api[`${listingName}PrevNextToken`] = undefined;

  return api;
};

export default useResource;
